import { clearPersistedCache, readPersistedCache, writePersistedCache } from "../../utils/persistedCache";
import type { ContactBook } from "./contactBookRules";

/**
 * ONE STORE FOR "WHICH BOOK AM I IN", SHARED BY EVERY SURFACE ON THE SCREEN.
 *
 * The third of the contact stores (`contactLookupStore` — who is this phone;
 * `contactDocumentStore` — the document of this contact), and the smallest: one answer per
 * account, `{ bookKey, mode, orgId }`, from `GET /contacts/book`. Every document-store
 * reader, every signal listener and every "is this row mine" comparison keys off it, so it
 * has to be ONE fetch per session shared by all of them — not a request per mounted card.
 *
 * ── The three states ────────────────────────────────────────────────────────────────
 *
 *   null, not in flight    nobody asked yet, or the last ask FAILED (`error` says so)
 *   null, in flight        the answer is on its way — a reader that needs the key waits
 *   a book                 the answer; re-asked once it is older than {@link STALE_AFTER_MS}
 *                          (in the background — the held answer keeps serving) and at once
 *                          when {@link ContactBookStore.invalidate} runs (a flip, a reversal)
 *
 * A failed ask never records a book, and never records "personal" either: for a pooled
 * member "personal" would key every card under a book with no documents in it. The reader
 * falls back to the email where a plausible key is enough (`ownBookKey`) and waits where it
 * is not.
 *
 * ── It belongs to ONE account, and it survives a reload ─────────────────────────────
 *
 * `bindAccount` drops everything when the signed-in user changes, as the other two stores
 * do. The last SUCCESSFUL answer is also kept in `localStorage` (`persistedCache`: stamped
 * with the account, expiring, never written from a failure) so the first frame after a
 * reload already knows the book — a pooled member's cards would otherwise wait one round
 * trip before they could even ask for a document. It is a layout/addressing hint, never an
 * access decision: the server resolves the scope on every request whatever this says.
 *
 * ── A reader that is not a hook ─────────────────────────────────────────────────────
 *
 * `whenContactBookSettled` (below the store) is the door for code that cannot subscribe —
 * the documents tab's Firestore layer — and whose address must be RIGHT: it waits for the
 * answer instead of reading whatever the store happens to hold at that instant.
 *
 * Pure: no React, no network, an injected clock and an injectable persistence seam.
 * `createContactBookStore` exists for the tests; the app uses the instance at the bottom.
 */

/** How long an answer is trusted before the next reader re-asks in the background. */
export const STALE_AFTER_MS = 5 * 60 * 1000;

const PERSIST_KEY = "webot:contactBook:v1";

export type ContactBookPersistence = {
  read: (email: string) => { value: ContactBook; at: number } | null;
  write: (email: string, value: ContactBook) => void;
  clear: () => void;
};

export const persistedCachePersistence: ContactBookPersistence = {
  read: (email) => readPersistedCache<ContactBook>(PERSIST_KEY, email),
  write: (email, value) => writePersistedCache(PERSIST_KEY, email, value),
  clear: () => clearPersistedCache(PERSIST_KEY)
};

export const noPersistence: ContactBookPersistence = {
  read: () => null,
  write: () => undefined,
  clear: () => undefined
};

export type ContactBookStore = {
  /** Idempotent. A CHANGE of account drops the answer and starts a new session. */
  bindAccount: (account: string | null) => void;
  /** The bound account, folded — what the fallback key is when nothing is held. */
  account: () => string | null;
  /** Identifies the bound account's lifetime. Capture it when asking; pass it back with the answer. */
  session: () => number;
  /** Moves on `invalidate` — mounted readers re-ask on a change. */
  epoch: () => number;
  read: () => ContactBook | null;
  /** When the held answer landed (ms); 0 while none has. */
  fetchedAt: () => number;
  isInFlight: () => boolean;
  /** The last FAILED ask, kept beside whatever answer is held. Cleared by the next answer. */
  error: () => Error | null;
  /** Held, and not yet older than the stale window. */
  isFresh: () => boolean;
  /**
   * The held answer predates the last `invalidate` — the book CHANGED under it (a flip, a
   * reversal) and the next answer is the one that counts. A time-stale answer is not
   * superseded: a book does not change by getting older.
   */
  isSuperseded: () => boolean;
  /** Should a reader ask: nothing held, or stale, or invalidated — and never while a request is out. */
  shouldFetch: () => boolean;
  /** Marks a request out. False when one already is — the caller then waits for it. */
  claim: () => boolean;
  /** The answer to a claim; ignored when the session moved underneath it. */
  record: (session: number, book: ContactBook) => void;
  /** A claim that failed: back to "not in flight", the held answer kept, the error noted. */
  release: (session: number, error: Error) => void;
  /** The book changed (a flip, a reversal): the held answer is stale now; readers re-ask at once. */
  invalidate: () => void;
  subscribe: (listener: () => void) => () => void;
};

export const createContactBookStore = (
  now: () => number = Date.now,
  persistence: ContactBookPersistence = persistedCachePersistence
): ContactBookStore => {
  let boundAccount: string | null = null;
  let currentSession = 0;
  let currentEpoch = 0;
  let held: ContactBook | null = null;
  let heldAt = 0;
  let heldEpoch = 0;
  let inFlight = false;
  let lastError: Error | null = null;
  const subscribers = new Set<() => void>();

  const notify = (): void => subscribers.forEach((listener) => listener());

  return {
    bindAccount: (account) => {
      const next = account?.trim().toLowerCase() || null;
      if (next === boundAccount) return;
      boundAccount = next;
      currentSession += 1;
      held = null;
      heldAt = 0;
      heldEpoch = currentEpoch;
      inFlight = false;
      lastError = null;
      if (!next) {
        // Signed out: the persisted answer goes too, so the next account on this browser
        // cannot be keyed under the previous one's book even for the frame before its own
        // request lands (the account stamp would refuse it anyway; this is the belt).
        persistence.clear();
        return;
      }
      // The first frame after a reload knows the book. Kept at ITS timestamp, so the
      // staleness check below decides honestly whether to revalidate behind it.
      const persisted = persistence.read(next);
      if (persisted) {
        held = persisted.value;
        heldAt = persisted.at;
      }
      // No `notify()`: this runs while a reader is rendering, and every reader re-renders
      // on the same auth change that brought it here.
    },

    account: () => boundAccount,
    session: () => currentSession,
    epoch: () => currentEpoch,
    read: () => held,
    fetchedAt: () => heldAt,
    isInFlight: () => inFlight,
    error: () => lastError,

    isFresh: () => held !== null && heldEpoch === currentEpoch && now() - heldAt < STALE_AFTER_MS,

    isSuperseded: () => held !== null && heldEpoch !== currentEpoch,

    shouldFetch: () => {
      if (!boundAccount || inFlight) return false;
      if (!held) return true;
      return heldEpoch !== currentEpoch || now() - heldAt >= STALE_AFTER_MS;
    },

    claim: () => {
      if (inFlight) return false;
      inFlight = true;
      return true;
    },

    record: (session, book) => {
      if (session !== currentSession) return;
      inFlight = false;
      lastError = null;
      held = book;
      heldAt = now();
      heldEpoch = currentEpoch;
      if (boundAccount) persistence.write(boundAccount, book);
      notify();
    },

    release: (session, error) => {
      if (session !== currentSession) return;
      inFlight = false;
      lastError = error;
      notify();
    },

    invalidate: () => {
      currentEpoch += 1;
      // The persisted copy is the previous book too; a flip is exactly the moment it is wrong.
      persistence.clear();
      notify();
    },

    subscribe: (listener) => {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    }
  };
};

/** How long a reader that is not a hook waits for the book before it goes on without an answer. */
export const BOOK_WAIT_MS = 5000;

type Schedule = (run: () => void, ms: number) => () => void;

const timerSchedule: Schedule = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/**
 * THE BOOK, FOR A READER THAT IS NOT A HOOK AND WHOSE ADDRESS MUST BE RIGHT.
 *
 * The documents tab builds Firestore paths outside React (a preload on row hover, a
 * listener, an upload). Reading `store.read()` at that instant is a race on a cold browser:
 * `/contacts/book` has not answered yet, the read is null, and a pooled member's documents
 * are addressed under their personal path — an empty list cached, a listener on the wrong
 * collection, an upload nobody else in the office can see. So this WAITS:
 *
 *   an answer is held                  that answer, at once — time-stale or not (the stale
 *                                      window revalidates behind it)
 *   nothing held, or the held answer   asks (`ask`: the API call lives with the hook, so it
 *   was invalidated by a flip          is handed in) or joins the request that is out, and
 *                                      resolves when it SETTLES — with the new answer, or,
 *                                      when the ask failed, with whatever is still held
 *   nobody is signed in                null, at once
 *   the account changed meanwhile,     null
 *   or `timeoutMs` passed
 *
 * Null is "no answer", never "personal": the caller decides what an unanswered book means
 * for it (the documents tab goes on with the signed-in email — the path it always used,
 * right for every personal book — rather than hang a Firestore-only tab on a server that
 * is not answering).
 */
export const whenContactBookSettled = (
  store: ContactBookStore,
  ask: () => void,
  options: { timeoutMs?: number; schedule?: Schedule } = {}
): Promise<ContactBook | null> => {
  const answer = (): ContactBook | null => (store.isSuperseded() ? null : store.read());
  const held = answer();
  if (held || !store.account()) return Promise.resolve(held);

  const session = store.session();
  return new Promise((resolve) => {
    let settled = false;
    let cancelTimer: () => void = () => undefined;
    let unsubscribe: () => void = () => undefined;
    const finish = (book: ContactBook | null): void => {
      if (settled) return;
      settled = true;
      cancelTimer();
      unsubscribe();
      resolve(book);
    };
    const check = (): void => {
      if (store.session() !== session) {
        finish(null);
        return;
      }
      const book = answer();
      // An answer — or no request left that could bring one: the ask failed, and whatever
      // is still held (an answer a flip superseded) is all there is.
      if (book) finish(book);
      else if (!store.isInFlight()) finish(store.read());
    };
    unsubscribe = store.subscribe(check);
    cancelTimer = (options.schedule ?? timerSchedule)(
      () => finish(store.session() === session ? store.read() : null),
      options.timeoutMs ?? BOOK_WAIT_MS
    );
    if (store.shouldFetch()) ask();
    check();
  });
};

/** The app's one instance. */
export const contactBookStore = createContactBookStore();
